import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Scale } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const SendDietModal = ({ athleteId, athleteName, onClose, onSent }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError(t('diets.error_only_pdf'));
      return;
    }
    setError('');
    setPdfFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPdfBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!title.trim()) { setError(t('diets.error_title_required')); return; }
    if (!pdfBase64) { setError(t('diets.error_pdf_required')); return; }
    setSending(true);
    setError('');
    try {
      const res = await apiFetch('/api/diets', {
        method: 'POST',
        body: JSON.stringify({
          athlete_id: athleteId,
          title: title.trim(),
          notes: notes.trim() || null,
          pdf_file: pdfBase64,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || t('diets.error_send'));
        return;
      }
      onSent?.();
      onClose();
    } catch {
      setError(t('diets.error_send'));
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Scale size={20} style={{ color: 'var(--primary)' }} />
            <h2 className="modal-title">
              {t('diets.send_title', { name: athleteName?.split(' ')[0] || athleteName })}
            </h2>
          </div>
          <button className="btn btn-icon btn-secondary" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '0 16px 16px' }}>
          <div className="form-group">
            <label className="form-label">{t('diets.title_label')}</label>
            <input
              className="input"
              placeholder={t('diets.title_placeholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('diets.notes_label')}{' '}
              <span style={{ color: 'var(--muted-foreground)', fontWeight: '400' }}>
                {t('common.optional')}
              </span>
            </label>
            <textarea
              className="input"
              placeholder={t('diets.notes_placeholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('diets.pdf_label')}</label>
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '10px', padding: '20px 16px',
              border: `2px dashed ${pdfFile ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px', cursor: 'pointer',
              background: pdfFile ? 'rgba(var(--primary-rgb),0.08)' : 'transparent',
              transition: 'all 0.2s ease',
            }}>
              <Upload size={20} style={{ color: pdfFile ? 'var(--primary)' : 'var(--muted-foreground)', flexShrink: 0 }} />
              <span style={{
                fontSize: '14px',
                color: pdfFile ? 'var(--primary)' : 'var(--muted-foreground)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {pdfFile ? pdfFile.name : t('diets.pdf_placeholder')}
              </span>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {error && (
            <p style={{ color: 'var(--destructive)', fontSize: '13px', marginBottom: '12px' }}>
              {error}
            </p>
          )}

          <button
            className="btn btn-primary w-full"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? t('diets.sending') : t('diets.send_btn')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SendDietModal;
